import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import CheckIcon from '@mui/icons-material/Check';

const PRIMARY = '#FF7518';
const DARK = '#4a2d08';
const DARK_BG = '#0f0f0f';

const pricingTiers = [
  {
    id: 'free',
    name: 'Community',
    price: 0,
    description: 'For individual developers and personal projects',
    popular: false,
    features: [
      '50 scans / month',
      '10 basic security rules',
      'Text summary output',
      'Community support (Discord)',
      '15-day trial of full features',
    ],
    cta: 'Start Free',
    ctaVariant: 'outlined' as const,
  },
  {
    id: 'starter',
    name: 'Starter',
    price: 99,
    description: 'Perfect for healthcare startups and seed-stage companies',
    popular: false,
    features: [
      '300 scans / month',
      '25 core security rules',
      'PDF report exports',
      'HIPAA basics coverage',
      'GDPR basics coverage',
      'Community + email support',
    ],
    cta: 'Start Free Scan',
    ctaVariant: 'outlined' as const,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 249,
    description: 'Perfect for growing and established healthcare teams',
    popular: true,
    features: [
      '1,200 scans / month',
      '75 advanced security rules',
      'JSON, CSV, PDF, executive summary',
      'Complete HIPAA + GDPR + HITRUST',
      'Multi-cloud support',
      '5 custom rules / month',
      'Advanced analytics & dashboards',
      '24h priority support',
    ],
    cta: 'Start Free Scan',
    ctaVariant: 'contained' as const,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 1299,
    description: 'For large healthcare organizations and enterprises',
    popular: false,
    features: [
      '10,000 scans / month',
      '99+ rules — all frameworks',
      'Audit-ready export packages',
      'White-label reports',
      'Team management & RBAC',
      '50 custom rules / month',
      'All CI/CD integrations',
      'Dedicated support engineer',
      'Quarterly compliance training',
    ],
    cta: 'Contact Sales',
    ctaVariant: 'outlined' as const,
  },
];

const faqs = [
  {
    q: 'What counts as a scan?',
    a: 'One scan = running baastream-scan against one target (file, directory, or log). Running many rules in a single invocation still counts as one scan.',
  },
  {
    q: 'Can I change plans at any time?',
    a: 'Yes. Upgrade or downgrade at any time. Upgrades take effect immediately; downgrades apply at the end of your billing cycle.',
  },
  {
    q: 'Is there a free trial for paid plans?',
    a: 'All new accounts get a 15-day full-feature trial (extended to 30 days for verified healthcare organizations). No credit card required.',
  },
  {
    q: 'Do you offer annual billing?',
    a: 'Yes. Annual billing saves 16% compared to monthly. Switch to annual any time from your billing settings.',
  },
  {
    q: 'What payment methods are accepted?',
    a: 'We accept all major credit and debit cards. Enterprise invoices can be paid by bank transfer.',
  },
  {
    q: 'Is there a refund policy?',
    a: 'Yes. See our Refund Policy for full details on eligibility and process.',
  },
];

export function PricingPage() {
  const navigate = useNavigate();
  const [billingAnnual, setBillingAnnual] = useState(false);

  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh' }}>
      {/* Minimal Nav */}
      <Box sx={{ bgcolor: DARK_BG, py: 2, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Container maxWidth="lg">
          <Box
            component="a"
            href="/"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, textDecoration: 'none' }}
          >
            <HealthAndSafetyIcon sx={{ color: PRIMARY, fontSize: 26 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
              BaaStream
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Hero */}
      <Box sx={{ bgcolor: DARK_BG, pb: { xs: 8, md: 10 }, pt: { xs: 6, md: 8 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Chip
            label="Pricing"
            size="small"
            sx={{ bgcolor: 'rgba(255,117,24,0.15)', color: PRIMARY, fontWeight: 600, mb: 3 }}
          />
          <Typography
            variant="h1"
            sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, fontWeight: 800, color: '#fff', lineHeight: 1.2, mb: 3 }}
          >
            Simple, transparent{' '}
            <Box component="span" sx={{ color: PRIMARY }}>
              pricing
            </Box>
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '1.1rem', lineHeight: 1.8, mb: 5 }}>
            All plans include unlimited users. No hidden fees. Cancel anytime.
          </Typography>

          {/* Billing toggle */}
          <Box sx={{ display: 'inline-flex', bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 3, p: 0.5 }}>
            <Button
              size="small"
              onClick={() => setBillingAnnual(false)}
              sx={{
                borderRadius: 2.5,
                px: 3,
                bgcolor: !billingAnnual ? '#fff' : 'transparent',
                color: !billingAnnual ? DARK : 'rgba(255,255,255,0.6)',
                fontWeight: 600,
                '&:hover': { bgcolor: !billingAnnual ? '#fff' : 'rgba(255,255,255,0.08)' },
              }}
            >
              Monthly
            </Button>
            <Button
              size="small"
              onClick={() => setBillingAnnual(true)}
              sx={{
                borderRadius: 2.5,
                px: 3,
                bgcolor: billingAnnual ? '#fff' : 'transparent',
                color: billingAnnual ? DARK : 'rgba(255,255,255,0.6)',
                fontWeight: 600,
                '&:hover': { bgcolor: billingAnnual ? '#fff' : 'rgba(255,255,255,0.08)' },
              }}
            >
              Annual{' '}
              <Chip
                label="Save 16%"
                size="small"
                sx={{ ml: 0.5, bgcolor: 'rgba(255,117,24,0.3)', color: PRIMARY, height: 18, fontSize: '0.65rem', fontWeight: 700 }}
              />
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Pricing Cards */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Grid container spacing={3} alignItems="stretch">
          {pricingTiers.map((tier) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={tier.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: tier.popular ? `2px solid ${PRIMARY}` : '1px solid rgba(0,0,0,0.08)',
                  position: 'relative',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 16px 40px rgba(0,0,0,0.12)' },
                }}
              >
                {tier.popular && (
                  <Box sx={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)' }}>
                    <Chip
                      label="Most Popular"
                      size="small"
                      sx={{ bgcolor: PRIMARY, color: '#fff', fontWeight: 700, px: 1 }}
                    />
                  </Box>
                )}
                <CardContent sx={{ p: 3.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: DARK, mb: 0.5 }}>
                    {tier.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888', mb: 2.5, display: 'block', minHeight: 32 }}>
                    {tier.description}
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    {tier.price === 0 ? (
                      <Typography variant="h3" sx={{ fontWeight: 800, color: DARK }}>
                        Free
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography variant="h3" sx={{ fontWeight: 800, color: DARK }}>
                          ${billingAnnual ? Math.round(tier.price * 0.833) : tier.price}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#888' }}>/mo</Typography>
                      </Box>
                    )}
                    {billingAnnual && tier.price > 0 && (
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        Billed annually
                      </Typography>
                    )}
                  </Box>
                  <List dense disablePadding sx={{ flexGrow: 1 }}>
                    {tier.features.map((f) => (
                      <ListItem key={f} disableGutters sx={{ py: 0.4 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <CheckIcon sx={{ fontSize: 15, color: tier.popular ? PRIMARY : '#4ade80' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={f}
                          primaryTypographyProps={{ variant: 'body2', color: '#555', lineHeight: 1.4 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Button
                    fullWidth
                    variant={tier.ctaVariant}
                    onClick={() => navigate(tier.id === 'enterprise' ? '/login' : '/register')}
                    sx={{
                      mt: 3,
                      ...(tier.popular
                        ? { bgcolor: PRIMARY, color: '#fff', '&:hover': { bgcolor: '#e05e00' } }
                        : {
                            borderColor: tier.ctaVariant === 'outlined' ? PRIMARY : undefined,
                            color: tier.ctaVariant === 'outlined' ? PRIMARY : undefined,
                          }),
                    }}
                  >
                    {tier.cta}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" sx={{ color: '#888' }}>
            Cancel anytime · All plans include unlimited users · Need a custom quote?{' '}
            <Box component="a" href="mailto:sales@baastream.com" sx={{ color: PRIMARY, textDecoration: 'none', fontWeight: 600 }}>
              Contact sales
            </Box>
          </Typography>
        </Box>

        <Divider sx={{ my: 8 }} />

        {/* Feature Comparison Note */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.7rem', md: '2.2rem' }, mb: 2 }}>
            What's included in every plan
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', maxWidth: 540, mx: 'auto', fontSize: '1.05rem' }}>
            Regardless of plan, BaaStream always runs entirely in your environment — no SaaS collectors,
            no firewall changes, and no PHI ever leaves your infrastructure.
          </Typography>
        </Box>

        <Grid container spacing={3} justifyContent="center">
          {[
            { label: 'Runs in your environment', detail: 'CLI-native. Your data stays yours.' },
            { label: 'TLS-encrypted results', detail: 'Scan results transmitted over TLS.' },
            { label: 'No PHI exposure', detail: 'BaaStream never touches your patient data.' },
            { label: 'Version-controlled rules', detail: 'Rule updates are transparent and auditable.' },
            { label: 'macOS, Linux, Windows', detail: 'Cross-platform CLI support.' },
            { label: 'Unlimited users', detail: 'All plans include your full team.' },
          ].map((item) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.label}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <CheckIcon sx={{ color: PRIMARY, mt: 0.3, flexShrink: 0 }} />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: DARK }}>
                    {item.label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {item.detail}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 8 }} />

        {/* FAQ */}
        <Box sx={{ maxWidth: 680, mx: 'auto' }}>
          <Typography variant="h2" sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.7rem', md: '2.2rem' }, mb: 5, textAlign: 'center' }}>
            Pricing FAQ
          </Typography>
          {faqs.map((faq) => (
            <Box key={faq.q} sx={{ mb: 4 }}>
              <Typography variant="body1" sx={{ fontWeight: 700, color: DARK, mb: 1 }}>
                {faq.q}
              </Typography>
              <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.8 }}>
                {faq.a}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: DARK_BG, py: 4, mt: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
              © 2025 Monks Do Work. All rights reserved. BaaStream is a product of Monks Do Work.
            </Typography>
            <Box component="a" href="/" sx={{ color: PRIMARY, fontSize: '0.8rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              ← Back to Home
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
